import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { BarChart3, Truck, Activity, Gauge, Bell, Building2, Users, Route, MapPin, Trophy, Download, FileText, Search, X, Layers, Check, ChevronDown, RotateCcw, Settings } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const PERIODS = [
  { v: "today", l: "Aujourd'hui" },
  { v: "yesterday", l: "Hier" },
  { v: "7d", l: "7 derniers jours" },
  { v: "30d", l: "30 derniers jours" },
  { v: "custom", l: "Personnalisé" },
];

const STATUS_COLORS = { moving: "#10B981", idling: "#F59E0B", stopped: "#EF4444", offline: "#71717A" };
const ALERT_LABEL = {
  exces_vitesse: "Excès vitesse", zone_entree: "Entrée zone", zone_sortie: "Sortie zone",
  arret_prolonge: "Arrêt prolongé", ralenti: "Ralenti", batterie: "Batterie", reseau: "Réseau",
  contact: "Contact", maintenance: "Maintenance", demarrage_non_autorise: "Démarrage NA",
  deconnexion: "Traceur déconnecté", poi_entree: "Arrivée POI", poi_sortie: "Départ POI",
  hors_trajet: "Hors trajet", dtc: "Défaut moteur", composee: "Alerte composée",
  // Éco-conduite (préfixe eco_ ou forme courte) + conducteur
  eco_harsh_accel: "Accélération brusque", harsh_accel: "Accélération brusque",
  eco_harsh_brake: "Freinage excessif", harsh_brake: "Freinage excessif",
  eco_cornering: "Virage brusque", cornering: "Virage brusque",
  eco_overspeed: "Excès vitesse (éco)", overspeed: "Excès vitesse (éco)",
  conducteur_non_identifie: "Conducteur non identifié",
};
const POI_CAT = { client: "Client", depot: "Dépôt", station: "Station", autre: "Autre" };

export default function AnalyticsPage() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [companies, setCompanies] = useState([]);
  const [units, setUnits] = useState([]);
  const [pools, setPools] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [poolFilter, setPoolFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState([]);
  const [vehQuery, setVehQuery] = useState("");
  const [vehOpen, setVehOpen] = useState(false);
  const [poiScore, setPoiScore] = useState(null);
  const [view, setView] = useState("all"); // all | eco | poi
  // Item 5 — édition des horaires de travail de l'entreprise (base du taux « heures ouvrées »)
  const [workOpen, setWorkOpen] = useState(false);
  const [workForm, setWorkForm] = useState({ work_days: [0, 1, 2, 3, 4], work_start: "07:00", work_end: "17:00" });
  const [savingWork, setSavingWork] = useState(false);
  const DAY_LBL = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const openWorkCfg = () => {
    const c = companies.find((x) => x.id === companyFilter);
    setWorkForm({ work_days: c?.work_days || [0, 1, 2, 3, 4], work_start: c?.work_start || "07:00", work_end: c?.work_end || "17:00" });
    setWorkOpen(true);
  };
  const saveWorkCfg = async () => {
    if (!companyFilter) return;
    setSavingWork(true);
    try {
      const c = companies.find((x) => x.id === companyFilter) || {};
      await api.put(`/companies/${companyFilter}`, { ...c, ...workForm });
      const r = await api.get("/companies"); setCompanies(r.data);
      setWorkOpen(false);
      load();
    } catch (e) { /* permission ou autre : laissé silencieux */ }
    setSavingWork(false);
  };
  const [ecoMode, setEcoMode] = useState("vehicle"); // P3 — vehicle | driver (classements séparés)

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setUnits(r.data)).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams({ period });
    if (period === "custom") { if (customStart) p.set("start", customStart); if (customEnd) p.set("end", customEnd); }
    if (companyFilter) p.set("company_id", companyFilter);
    if (poolFilter) p.set("pool_id", poolFilter);
    if (unitFilter.length) p.set("vehicle_ids", unitFilter.join(","));
    return p;
  }, [period, customStart, customEnd, companyFilter, poolFilter, unitFilter]);

  const customReady = period !== "custom" || (customStart && customEnd);

  const load = useCallback(async () => {
    if (!customReady) return;
    const r = await api.get(`/analytics/overview?${buildParams().toString()}`);
    setD(r.data);
  }, [buildParams, customReady]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    if (!customReady) return;
    api.get(`/analytics/poi-scoring?${buildParams().toString()}`).then((r) => setPoiScore(r.data)).catch(() => {});
  }, [buildParams, customReady]);

  const resetFilters = () => {
    setPeriod("today"); setCustomStart(""); setCustomEnd("");
    setCompanyFilter(""); setPoolFilter(""); setUnitFilter([]); setVehQuery("");
  };

  if (!d) return <Layout><div className="p-6 text-muted-foreground" data-testid="analytics-loading">Chargement des statistiques…</div></Layout>;

  const k = d.kpis;
  const periodLabel = PERIODS.find((p) => p.v === period)?.l || "";

  // Classement éco-conduite : deux vues séparées (véhicule / conducteur) ; meilleur -> moins bon.
  const ecoData = d.eco_scores || {};
  const ecoList = (ecoMode === "driver" ? ecoData.drivers : ecoData.vehicles) || [];
  const ecoMinKm = ecoData.min_km;
  const ecoSorted = [...ecoList].sort((a, b) => (b.score == null ? -1 : b.score) - (a.score == null ? -1 : a.score));
  const topItem = ecoSorted.find((s) => s.score != null) || null;
  const topKey = topItem ? (topItem.driver_id || topItem.vehicle_id) + topItem.attributed_to : null;

  const companyName = companyFilter ? (companies.find((c) => c.id === companyFilter)?.name || "") : "Toutes compagnies";
  const buildEcoRows = () => ecoSorted.map((s, i) => [i + 1, s.name, s.attributed_to === "vehicle" ? "Véhicule" : "Conducteur", s.score == null ? (s.score_reason || "—") : s.score, s.harsh_accels ?? 0, s.harsh_brakes ?? 0, s.cornerings ?? 0, s.speedings ?? 0, s.idle_min ?? 0, s.km ?? 0, s.raw_km ?? 0]);
  const buildPoiRows = () => (poiScore?.pois || []).map((p, i) => [i + 1, p.name, POI_CAT[p.category] || p.category, p.visits, p.visits_per_day, p.avg_stop_min, p.unique_vehicles]);

  const saveBlob = (blob, name) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1500); };

  const exportCsv = () => {
    const lines = [`Tableau de bord analytique;${companyName};Période ${periodLabel}`, ""];
    const push = (title, header, rows) => { lines.push(title, header.join(";"), ...rows.map((r) => r.join(";")), ""); };
    if (view !== "poi") push(`Éco-conduite — ${ecoMode === "driver" ? "par conducteur" : "par véhicule"} (distance filtrée; brut = GPS non filtré)`, ["#", "Conducteur/Véhicule", "Type", "Score /100", "Accél.", "Freinages", "Virages", "Excès", "Ralenti (min)", "Km (filtré)", "Km (brut GPS)"], buildEcoRows());
    if (view !== "eco") push("Scoring POI", ["#", "POI", "Catégorie", "Visites", "Fréq/j", "Arrêt moyen (min)", "Unités"], buildPoiRows());
    saveBlob(new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" }), `analytique_${view}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14); doc.text("Tableau de bord analytique", 14, 16);
    doc.setFontSize(10); doc.setTextColor(120); doc.text(`${companyName}  -  Période ${periodLabel}`, 14, 23);
    let y = 30;
    if (view !== "poi") {
      autoTable(doc, { startY: y, head: [["#", "Conducteur/Véhicule", "Type", "Score", "Accél.", "Frein.", "Virages", "Excès", "Ralenti", "Km (filtré)", "Km (brut)"]], body: buildEcoRows(), styles: { fontSize: 8 }, headStyles: { fillColor: [23, 162, 184] } });
      y = (doc.lastAutoTable?.finalY || y) + 8;
    }
    if (view !== "eco") {
      autoTable(doc, { startY: y, head: [["#", "POI", "Catégorie", "Visites", "Fréq/j", "Arrêt moy.", "Unités"]], body: buildPoiRows(), styles: { fontSize: 8 }, headStyles: { fillColor: [245, 130, 31] } });
    }
    doc.save(`analytique_${view}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="label-mono">Aide à la décision</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-3"><BarChart3 className="w-7 h-7 brand-teal" /> Tableau de bord analytique</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 flex-wrap" data-testid="analytics-period">
            {PERIODS.map((o) => (
              <button key={o.v} onClick={() => setPeriod(o.v)} data-testid={`period-${o.v}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${period === o.v ? "border-[#17A2B8] text-[#2ec5db] bg-[#17A2B8]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{o.l}</button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="analytics-custom-range">
              <input type="date" value={customStart} max={customEnd || undefined} onChange={(e) => setCustomStart(e.target.value)} data-testid="custom-start"
                className="bg-secondary/40 border border-border rounded-md px-2 py-1.5 text-xs" />
              <span className="text-muted-foreground text-xs">→</span>
              <input type="date" value={customEnd} min={customStart || undefined} onChange={(e) => setCustomEnd(e.target.value)} data-testid="custom-end"
                className="bg-secondary/40 border border-border rounded-md px-2 py-1.5 text-xs" />
            </div>
          )}
          <div className="flex gap-1" data-testid="analytics-export">
            <button onClick={exportCsv} data-testid="export-csv-btn" title="Exporter en CSV"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-border text-muted-foreground hover:text-[#F5821F] hover:border-[#F5821F] transition-colors"><Download className="w-3.5 h-3.5" /> CSV</button>
            <button onClick={exportPdf} data-testid="export-pdf-btn" title="Exporter en PDF"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-border text-muted-foreground hover:text-[#EF4444] hover:border-[#EF4444] transition-colors"><FileText className="w-3.5 h-3.5" /> PDF</button>
          </div>
        </div>
      </div>

      {/* Filtres : vue / société / pool / véhicule (recherche) */}
      <div className="glass rounded-lg p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="analytics-filters">
        <div className="flex gap-1" data-testid="analytics-view">
          {[{ v: "all", l: "Tout" }, { v: "eco", l: "Éco-conduite" }, { v: "poi", l: "Scoring POI" }].map((o) => (
            <button key={o.v} onClick={() => setView(o.v)} data-testid={`view-${o.v}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${view === o.v ? "border-[#17A2B8] text-[#2ec5db] bg-[#17A2B8]/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{o.l}</button>
          ))}
        </div>
        <span className="w-px h-5 bg-border hidden sm:block" />

        {/* Société (masqué si l'utilisateur n'a qu'une seule société) */}
        {companies.length > 1 && (
          <select data-testid="filter-company" value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPoolFilter(""); setUnitFilter([]); }}
            className="bg-secondary/40 border border-border rounded-md px-3 py-1.5 text-sm">
            <option value="">Toutes les sociétés</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        {/* Pool / groupe */}
        <select data-testid="filter-pool" value={poolFilter}
          onChange={(e) => { setPoolFilter(e.target.value); setUnitFilter([]); }}
          className="bg-secondary/40 border border-border rounded-md px-3 py-1.5 text-sm">
          <option value="">Tous les groupes</option>
          {pools.filter((p) => !companyFilter || p.company_id === companyFilter).map((p) => (
            <option key={p.id} value={p.id}>{p.name} ({(p.vehicle_ids || []).length})</option>
          ))}
        </select>

        {/* Véhicule (recherche nom + immatriculation) */}
        <Popover open={vehOpen} onOpenChange={setVehOpen}>
          <PopoverTrigger asChild>
            <button data-testid="filter-vehicle-trigger"
              className="flex items-center gap-2 bg-secondary/40 border border-border rounded-md px-3 py-1.5 text-sm hover:border-[#17A2B8] transition-colors">
              <Truck className="w-3.5 h-3.5 text-muted-foreground" />
              {unitFilter.length === 0 ? "Tous les véhicules" : `${unitFilter.length} véhicule${unitFilter.length > 1 ? "s" : ""}`}
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-0" data-testid="filter-vehicle-panel">
            <div className="p-2 border-b border-border flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input autoFocus value={vehQuery} onChange={(e) => setVehQuery(e.target.value)} data-testid="vehicle-search"
                placeholder="Nom ou immatriculation…" className="flex-1 bg-transparent text-sm outline-none" />
              {unitFilter.length > 0 && <button onClick={() => setUnitFilter([])} data-testid="vehicle-clear-all" className="text-xs text-[#F5821F] hover:underline">Effacer</button>}
            </div>
            <div className="max-h-64 overflow-y-auto py-1" data-testid="vehicle-options">
              {(() => {
                const scoped = units.filter((u) => (!companyFilter || u.company_id === companyFilter));
                const s = vehQuery.trim().toLowerCase();
                const res = scoped.filter((u) => !s || (u.name || "").toLowerCase().includes(s) || (u.plate || "").toLowerCase().includes(s));
                if (res.length === 0) return <p className="px-3 py-4 text-xs text-muted-foreground text-center">Aucun véhicule</p>;
                return res.slice(0, 100).map((u) => {
                  const on = unitFilter.includes(u.id);
                  const poolName = pools.find((p) => (p.vehicle_ids || []).includes(u.id))?.name;
                  return (
                    <button key={u.id} data-testid={`vehicle-option-${u.id}`}
                      onClick={() => setUnitFilter((f) => on ? f.filter((x) => x !== u.id) : [...f, u.id])}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-black/5 transition-colors">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#17A2B8] border-[#17A2B8]" : "border-border"}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
                      <span className="flex-1 min-w-0 truncate">{u.name} <span className="text-muted-foreground">· {u.plate}</span></span>
                      {poolName && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#17A2B8]/15 text-[#2ec5db] flex items-center gap-1 shrink-0"><Layers className="w-2.5 h-2.5" />{poolName}</span>}
                    </button>
                  );
                });
              })()}
            </div>
          </PopoverContent>
        </Popover>

        <button onClick={resetFilters} data-testid="filter-reset"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-border text-muted-foreground hover:text-[#F5821F] hover:border-[#F5821F] transition-colors ml-auto">
          <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
        </button>
      </div>

      {/* Chips de filtres actifs */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="active-filter-chips">
        <Chip testid="chip-period" label={periodLabel + (period === "custom" && customStart && customEnd ? ` (${customStart} → ${customEnd})` : "")} />
        {companyFilter && <Chip testid="chip-company" label={companyName} onRemove={() => { setCompanyFilter(""); setPoolFilter(""); }} />}
        {poolFilter && <Chip testid="chip-pool" label={`Groupe : ${pools.find((p) => p.id === poolFilter)?.name || ""}`} onRemove={() => setPoolFilter("")} />}
        {unitFilter.map((id) => (
          <Chip key={id} testid={`chip-unit-${id}`} label={units.find((u) => u.id === id)?.name || id}
            onRemove={() => setUnitFilter((f) => f.filter((x) => x !== id))} />
        ))}
      </div>

      {/* KPIs */}
      {view === "all" && (<>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-6" data-testid="analytics-kpis">
        <KPI icon={Truck} label="Unités" value={k.units} />
        <KPI icon={Activity} label="Actives" value={k.active} color="#10B981" />
        <KPI icon={Gauge} label="Taux util. moy." value={k.avg_utilization == null ? "—" : `${k.avg_utilization}%`} color="#17A2B8"
             sub={k.util_coverage ? `${k.util_coverage.with_data} renseigné${k.util_coverage.with_data > 1 ? "s" : ""} sur ${k.util_coverage.total}` : undefined} />
        <KPI icon={Route} label="Km cumulés (total compteurs)" value={k.total_km.toLocaleString("fr-FR")} color="#F5821F" />
        <KPI icon={Building2} label="Compagnies" value={k.companies} />
        <KPI icon={Users} label="Conducteurs" value={k.drivers} />
        <KPI icon={Bell} label="Alertes non lues" value={k.unack_alerts} color="#EF4444" />
        <KPI icon={Bell} label="Alertes du jour" value={k.alerts_today} color="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <Card title="Répartition de la flotte par statut" testid="chart-status">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={d.by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                {d.by_status.map((e) => <Cell key={e.key} fill={STATUS_COLORS[e.key]} />)}
              </Pie>
              <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, color: "#f1f5f9" }} labelStyle={{ color: "#f1f5f9" }} itemStyle={{ color: "#f1f5f9" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Kilomètres par compagnie" testid="chart-company">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.by_company}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="company" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
              <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} />
              <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, color: "#f1f5f9" }} labelStyle={{ color: "#f1f5f9" }} itemStyle={{ color: "#f1f5f9" }} />
              <Bar dataKey="km" name="Km" fill="#17A2B8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Alertes par type" testid="chart-alerts">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.alerts_by_type.map((a) => ({ ...a, label: ALERT_LABEL[a.type] || a.type }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis dataKey="label" tick={{ fill: "#a1a1aa", fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} />
              <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, color: "#f1f5f9" }} labelStyle={{ color: "#f1f5f9" }} itemStyle={{ color: "#f1f5f9" }} />
              <Bar dataKey="count" name="Alertes" fill="#F5821F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title={`Top 5 unités — distance (${periodLabel})`} testid="chart-top">
          <div className="space-y-2" data-testid="top-km-list">
            {d.top_km.map((t, i) => {
              const max = d.top_km[0]?.km || 1;
              const nd = t.no_data || t.km == null;
              return (
                <div key={t.plate} className="flex items-center gap-3">
                  <span className="w-6 text-center font-bold text-sm" style={{ color: i === 0 ? "#F5821F" : "#71717A" }}>{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1"><span className="font-semibold">{t.vehicle}</span><span className="data text-muted-foreground">{nd ? "— (aucune donnée)" : `${t.km} km`}</span></div>
                    <div className="h-2 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full bg-[#17A2B8]" style={{ width: `${nd || !max ? 0 : (t.km / max) * 100}%` }} /></div>
                  </div>
                </div>
              );
            })}
            {d.top_km.every((t) => t.no_data || t.km == null || t.km === 0) && <p className="text-xs text-muted-foreground">Pas encore de distance parcourue sur la période.</p>}
          </div>
        </Card>
      </div>

      {/* Taux d'utilisation */}
      <Card title="Taux d'utilisation par unité" testid="util-table">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-[11px] text-muted-foreground" data-testid="util-rule">
            {k.util_rule?.label || "Temps en mouvement ÷ temps suivi"} · Période : {periodLabel}
            {k.util_rule?.mode === "work_hours" && !k.util_rule?.no_work_hours ? ` · ${k.util_rule.work_hours} h ouvrées` : ""}
            {k.util_rule?.mode === "work_hours" && k.util_rule?.off_hours_min != null && k.util_rule.off_hours_min > 0
              ? ` · Mouvement hors horaires : ${k.util_rule.off_hours_min >= 60 ? `${Math.floor(k.util_rule.off_hours_min / 60)} h ${String(k.util_rule.off_hours_min % 60).padStart(2, "0")}` : `${k.util_rule.off_hours_min} min`}`
              : ""}
          </p>
          <Popover open={workOpen} onOpenChange={setWorkOpen}>
            <PopoverTrigger asChild>
              <button onClick={openWorkCfg} disabled={!companyFilter} data-testid="util-workcfg-btn"
                title={companyFilter ? "Configurer les horaires de travail" : "Sélectionnez une compagnie pour configurer ses horaires"}
                className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${companyFilter ? "border-border hover:text-[#17A2B8]" : "border-border text-muted-foreground/40 cursor-not-allowed"}`}>
                <Settings className="w-3.5 h-3.5" /> Horaires de travail
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-3" data-testid="util-workcfg-panel">
              <p className="label-mono">Horaires de travail de l'entreprise</p>
              <div className="flex flex-wrap gap-1">
                {DAY_LBL.map((lbl, i) => (
                  <button key={i} data-testid={`workcfg-day-${i}`}
                    onClick={() => setWorkForm((f) => ({ ...f, work_days: f.work_days.includes(i) ? f.work_days.filter((d) => d !== i) : [...f.work_days, i] }))}
                    className={`w-9 py-1 text-xs rounded border transition-colors ${workForm.work_days.includes(i) ? "border-[#17A2B8] bg-[#17A2B8]/10 text-[#17A2B8]" : "border-border text-muted-foreground"}`}>{lbl}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs">De</label>
                <input type="time" data-testid="workcfg-start" value={workForm.work_start} onChange={(e) => setWorkForm((f) => ({ ...f, work_start: e.target.value }))} className="flex-1 bg-secondary/60 border border-border rounded px-2 py-1 text-sm" />
                <label className="text-xs">à</label>
                <input type="time" data-testid="workcfg-end" value={workForm.work_end} onChange={(e) => setWorkForm((f) => ({ ...f, work_end: e.target.value }))} className="flex-1 bg-secondary/60 border border-border rounded px-2 py-1 text-sm" />
              </div>
              {workForm.work_end <= workForm.work_start && (
                <p className="text-[11px] text-[#17A2B8]" data-testid="workcfg-overnight-hint">Plage de nuit : fin le lendemain (ex. 22:00 → 06:00).</p>
              )}
              <button onClick={saveWorkCfg} disabled={savingWork} data-testid="workcfg-save"
                className="w-full bg-[#17A2B8] text-white text-sm font-semibold py-1.5 rounded-md hover:bg-[#148a9c] transition-colors disabled:opacity-60">
                {savingWork ? "Enregistrement…" : "Enregistrer"}
              </button>
            </PopoverContent>
          </Popover>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="label-mono text-left"><tr><th className="py-2 pr-4">Unité</th><th className="py-2 pr-4">Immat.</th><th className="py-2 pr-4">Distance</th><th className="py-2 pr-4">Taux d'utilisation</th><th className="py-2">Hors horaires</th></tr></thead>
            <tbody className="data">
              {d.utilization.map((u) => {
                const nd = u.rate == null;
                const workMode = k.util_rule?.mode === "work_hours";
                const noWork = workMode && k.util_rule?.no_work_hours;
                const offTxt = u.off_min == null ? "—" : (u.off_min >= 60 ? `${Math.floor(u.off_min / 60)} h ${String(u.off_min % 60).padStart(2, "0")}` : `${u.off_min} min`);
                return (
                <tr key={u.plate || u.vehicle} className="border-t border-border" data-testid={`util-row-${(u.plate || u.vehicle || "").replace(/\s/g, "")}`}>
                  <td className="py-2 pr-4 font-semibold">{u.vehicle}</td>
                  <td className="py-2 pr-4">{u.plate}</td>
                  <td className="py-2 pr-4">{u.no_data ? "—" : `${u.km} km`}</td>
                  <td className="py-2 pr-4">
                    {nd ? (
                      <span className="text-xs text-muted-foreground" title={noWork ? "Aucune heure ouvrée sur la période sélectionnée" : (u.no_data ? "Aucune donnée GPS sur la période" : "Taux non calculable")}>
                        — <span className="opacity-70">{noWork ? "aucune heure ouvrée" : "aucune donnée"}</span>
                      </span>
                    ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, u.rate)}%`, background: u.anomaly ? "#8B5CF6" : u.rate > 60 ? "#10B981" : u.rate > 25 ? "#F59E0B" : "#EF4444" }} /></div>
                      <span className="text-xs">{u.rate}%{u.anomaly ? <span className="ml-1 text-[#8B5CF6] font-semibold" title="Taux > 100 % : anomalie de calcul ou mouvement incohérent à vérifier">⚠</span> : null}</span>
                    </div>
                    )}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground" data-testid={`util-offhours-${(u.plate || u.vehicle || "").replace(/\s/g, "")}`}>
                    {workMode ? offTxt : "—"}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </>)}

      {/* Eco-score conducteurs */}
      {(view === "all" || view === "eco") && (
      <div className="mt-6">
        <Card title="Classement éco-conduite (du meilleur au moins bon)" testid="chart-eco">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="eco-mode-toggle">
              {[{ v: "vehicle", l: "Par véhicule" }, { v: "driver", l: "Par conducteur" }].map((o) => (
                <button key={o.v} onClick={() => setEcoMode(o.v)} data-testid={`eco-mode-${o.v}`}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${ecoMode === o.v ? "bg-[#17A2B8] text-white" : "bg-secondary/40 text-muted-foreground hover:text-foreground"}`}>{o.l}</button>
              ))}
            </div>
            <span className="text-[10px] text-muted-foreground" data-testid="eco-method-note">
              Distance de référence = filtrée (comme Activité/Rapports){ecoMinKm != null ? ` · score requis ≥ ${ecoMinKm} km` : ""}
            </span>
          </div>
          {ecoSorted.length ? (
            <div className="space-y-3" data-testid="eco-list">
              {ecoSorted.map((s, idx) => {
                const c = s.score == null ? "#71717A" : s.score >= 80 ? "#10B981" : s.score >= 60 ? "#F59E0B" : "#EF4444";
                const key = (s.driver_id || s.vehicle_id) + s.attributed_to;
                const isTop = key === topKey;
                return (
                  <div key={key} data-testid={`eco-${s.driver_id || s.vehicle_id}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold flex items-center gap-1.5">
                        <span className="w-5 text-center font-bold" style={{ color: idx === 0 ? "#F5821F" : "#71717A" }}>{idx + 1}</span>
                        {s.name}
                        {isTop && (
                          <span data-testid="eco-top-badge" className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#F5821F]/15 text-[#F5821F] flex items-center gap-1" title="Meilleur score sur la période">
                            <Trophy className="w-3 h-3" /> {ecoMode === "driver" ? "Top conducteur" : "Top unité"}
                          </span>
                        )}
                        {ecoMode === "driver" && s.identified === false
                          ? <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600" title="Trajets sans conducteur identifié">non identifié</span>
                          : (ecoMode === "driver" && s.vehicles?.length ? <span className="text-muted-foreground font-normal">· {s.vehicles.length > 1 ? `${s.vehicles.length} véhicules` : s.vehicles[0]}</span> : null)}
                        {ecoMode === "vehicle" && s.plate ? <span className="text-muted-foreground font-normal">· {s.plate}</span> : null}
                      </span>
                      <span className="data font-bold" style={{ color: c }} title={s.score == null ? (s.score_reason || "") : ""}>{s.score == null ? (s.score_reason || "—") : `${s.score}/100`}</span>
                    </div>
                    <div className="h-2 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${s.score || 0}%`, background: c }} /></div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 label-mono text-[10px]"><span>Accél.: {s.harsh_accels ?? 0}</span><span>Freinages: {s.harsh_brakes}</span><span>Virages: {s.cornerings ?? 0}</span><span>Excès: {s.speedings}</span><span>Ralenti: {s.idle_min} min</span><span>{s.km} km</span><span className="opacity-60" title="Distance brute GPS (tous points, non filtrée) — information technique uniquement.">brut GPS {s.raw_km} km</span></div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-xs text-muted-foreground">Aucune donnée éco-conduite sur ce périmètre.</p>}
        </Card>
      </div>
      )}

      {(view === "all" || view === "poi") && (
      <div className="mt-6">
        <Card title="Scoring des POI — les plus visités, fréquence & temps d'arrêt moyen" testid="chart-poi-scoring">
          {poiScore && poiScore.pois.length ? (
            <div className="overflow-x-auto" data-testid="poi-score-table">
              <table className="w-full text-sm">
                <thead className="label-mono text-left"><tr>
                  <th className="py-2 pr-3">#</th><th className="py-2 pr-4">POI</th><th className="py-2 pr-4">Catégorie</th>
                  <th className="py-2 pr-4">Visites</th><th className="py-2 pr-4">Fréq./j</th><th className="py-2 pr-4">Arrêt moyen</th><th className="py-2">Unités</th>
                </tr></thead>
                <tbody className="data">
                  {poiScore.pois.map((p, i) => {
                    const max = poiScore.pois[0]?.visits || 1;
                    return (
                      <tr key={p.poi_id} className="border-t border-border" data-testid={`poi-score-row-${i}`}>
                        <td className="py-2 pr-3 font-bold" style={{ color: i === 0 ? "#F5821F" : "#71717A" }}>{i + 1}</td>
                        <td className="py-2 pr-4 font-semibold"><span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 brand-orange" />{p.name}</span></td>
                        <td className="py-2 pr-4"><span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#17A2B822", color: "#2ec5db" }}>{POI_CAT[p.category] || p.category}</span></td>
                        <td className="py-2 pr-4"><div className="flex items-center gap-2"><div className="w-20 h-2 rounded-full bg-black/5 overflow-hidden"><div className="h-full rounded-full bg-[#17A2B8]" style={{ width: `${(p.visits / max) * 100}%` }} /></div><span>{p.visits}</span></div></td>
                        <td className="py-2 pr-4">{p.visits_per_day}</td>
                        <td className="py-2 pr-4">{p.avg_stop_min} min</td>
                        <td className="py-2">{p.unique_vehicles}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs text-muted-foreground">Aucune visite de POI sur la période (créez des POI avec zone/rayon dans le Live Tracking).</p>}
        </Card>
      </div>
      )}
    </Layout>
  );
}

const Chip = ({ label, onRemove, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#17A2B8]/10 text-[#2ec5db] border border-[#17A2B8]/30">
    {label}
    {onRemove && <button onClick={onRemove} data-testid={`${testid}-remove`} className="hover:text-[#F5821F] transition-colors"><X className="w-3 h-3" /></button>}
  </span>
);

const KPI = ({ icon: Icon, label, value, color, sub }) => (
  <div className="glass rounded-lg px-4 py-3" data-testid={`kpi-${label}`}>
    <div className="flex items-center gap-2 label-mono"><Icon className="w-3.5 h-3.5" style={{ color: color || "#a1a1aa" }} /> {label}</div>
    <p className="text-2xl font-extrabold data mt-1" style={{ color: color || "#fff" }}>{value}</p>
    {sub && <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="kpi-sub">{sub}</p>}
  </div>
);

const Card = ({ title, children, testid }) => (
  <div className="glass rounded-lg p-4" data-testid={testid}>
    <h2 className="text-sm font-bold mb-3">{title}</h2>
    {children}
  </div>
);
